/**
 * Add/Edit Tax Rate dialog (DNKADM-0051 U3) — a MUI `Dialog` that creates/updates a
 * per-location `v2_tax_rate` via `createTaxRate` / `updateTaxRate` (handoff §5.2).
 *
 * Fields: **name** ("State"/"City", required), **ratePercent** (required, 0–100, two
 * decimals — DECIMAL(5,2)), and optional **description** / **taxId** / **taxAgency**.
 * **No inclusive-tax toggle** and **no per-service rate selection** — tax is always
 * exclusive and all-or-none (§5.2); those controls do not exist.
 *
 * Unique on `(location, name)` is enforced client-side as a WARNING (a name already
 * used by another active rate blocks Save with an inline message) and the server
 * `409` is surfaced inline as the authority. On success the parent **refetches**
 * (the caller's `onSaved`).
 */
import { useId, useState } from 'react';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  InputAdornment,
  Stack,
  TextField,
} from '@mui/material';
import {
  createTaxRate,
  updateTaxRate,
  type TaxRate,
  type TaxRateInput,
} from '@/api/taxRates';
import type { ShowToast } from '@/components/toastContext';
import { classifyFormError } from '@/api/screenError';

export interface TaxRateDialogProps {
  /** Whether the dialog is open. */
  open: boolean;
  /** The owning location id. */
  locationId: string;
  /** The tax rate being edited, or `null` to add a new one. */
  rate: TaxRate | null;
  /**
   * Names already used by OTHER rates at this location (case-insensitive) — a
   * client-side warning on the unique `(location, name)` rule. The edited rate's own
   * name is excluded by the caller.
   */
  usedNames: readonly string[];
  /** Close the dialog without saving. */
  onClose: () => void;
  /** Called after a successful create/update so the caller can refetch the list. */
  onSaved: () => void;
  /** Raise a non-blocking toast (upstream-error). */
  showToast: ShowToast;
}

interface FieldErrors {
  name?: string;
  ratePercent?: string;
}

export function TaxRateDialog(props: TaxRateDialogProps) {
  if (!props.open) return null;
  return <TaxRateDialogForm key={props.rate?.id ?? 'new'} {...props} />;
}

/** Trim a string and return `undefined` when empty (omit the optional key). */
function optional(value: string): string | undefined {
  const trimmed = value.trim();
  return trimmed === '' ? undefined : trimmed;
}

/**
 * Parse a percentage string to a number in 0–100 with at most two decimals, or null
 * when invalid. Mirrors the DECIMAL(5,2) percentage scale (§5.2).
 */
function parsePercent(raw: string): number | null {
  const text = raw.trim();
  if (text === '') return null;
  if (!/^\d{1,3}(\.\d{1,2})?$/.test(text)) return null;
  const n = Number(text);
  if (!Number.isFinite(n) || n < 0 || n > 100) return null;
  return n;
}

function TaxRateDialogForm({
  locationId,
  rate,
  usedNames,
  onClose,
  onSaved,
  showToast,
}: Omit<TaxRateDialogProps, 'open'>) {
  const ids = useId();
  const isEdit = rate !== null;

  const [name, setName] = useState(rate?.name ?? '');
  const [ratePercent, setRatePercent] = useState(rate ? String(rate.ratePercent) : '');
  const [description, setDescription] = useState(rate?.description ?? '');
  const [taxId, setTaxId] = useState(rate?.taxId ?? '');
  const [taxAgency, setTaxAgency] = useState(rate?.taxAgency ?? '');

  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  const [serverError, setServerError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  function validate(): TaxRateInput | null {
    const errors: FieldErrors = {};
    const trimmedName = name.trim();
    if (trimmedName === '') {
      errors.name = 'Enter a tax name (e.g. State or City).';
    } else if (usedNames.some((n) => n.toLowerCase() === trimmedName.toLowerCase())) {
      // Client-side warning on the unique (location, name) rule; the server 409 is
      // the authority and is surfaced inline too.
      errors.name = 'A tax rate with this name already exists at this location.';
    }

    const percent = parsePercent(ratePercent);
    if (percent === null) {
      errors.ratePercent = 'Enter a percentage from 0 to 100 (up to two decimals).';
    }

    if (errors.name || errors.ratePercent) {
      setFieldErrors(errors);
      return null;
    }
    setFieldErrors({});

    const input: TaxRateInput = { name: trimmedName, ratePercent: percent as number };
    const d = optional(description);
    if (d !== undefined) input.description = d;
    const t = optional(taxId);
    if (t !== undefined) input.taxId = t;
    const a = optional(taxAgency);
    if (a !== undefined) input.taxAgency = a;
    return input;
  }

  async function handleSave() {
    setServerError(null);
    const input = validate();
    if (!input) return;
    setSaving(true);
    try {
      if (isEdit && rate) await updateTaxRate(locationId, rate.id, input);
      else await createTaxRate(locationId, input);
      // DNKADM-0320: confirm the save (after the write resolves; one toast per action).
      showToast(isEdit ? 'Tax rate saved' : 'Tax rate added', 'success');
      onSaved();
    } catch (err) {
      const view = classifyFormError(
        err,
        'The tax rate couldn’t be saved. Please try again.',
      );
      if (view.kind === 'inline') setServerError(view.message);
      else showToast(view.message, 'error');
    } finally {
      setSaving(false);
    }
  }

  const titleId = `${ids}-title`;

  return (
    <Dialog open onClose={onClose} aria-labelledby={titleId} maxWidth="sm" fullWidth>
      <DialogTitle id={titleId}>{isEdit ? 'Edit tax rate' : 'Add tax rate'}</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2.5} sx={{ pt: 0.5 }}>
          <Stack direction="row" spacing={2}>
            <TextField
              label="Name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              disabled={saving}
              size="small"
              required
              sx={{ flex: 2 }}
              error={Boolean(fieldErrors.name)}
              helperText={fieldErrors.name}
              slotProps={{ htmlInput: { 'aria-label': 'Tax name' } }}
            />
            <TextField
              label="Rate"
              value={ratePercent}
              onChange={(e) => setRatePercent(e.target.value)}
              disabled={saving}
              size="small"
              required
              sx={{ flex: 1 }}
              error={Boolean(fieldErrors.ratePercent)}
              helperText={fieldErrors.ratePercent}
              slotProps={{
                input: { endAdornment: <InputAdornment position="end">%</InputAdornment> },
                htmlInput: { inputMode: 'decimal', 'aria-label': 'Rate percent' },
              }}
            />
          </Stack>
          <TextField
            label="Description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            disabled={saving}
            size="small"
            slotProps={{ htmlInput: { 'aria-label': 'Description' } }}
          />
          <Stack direction="row" spacing={2}>
            <TextField
              label="Tax ID"
              value={taxId}
              onChange={(e) => setTaxId(e.target.value)}
              disabled={saving}
              size="small"
              sx={{ flex: 1 }}
              slotProps={{ htmlInput: { 'aria-label': 'Tax ID' } }}
            />
            <TextField
              label="Tax agency"
              value={taxAgency}
              onChange={(e) => setTaxAgency(e.target.value)}
              disabled={saving}
              size="small"
              sx={{ flex: 1 }}
              slotProps={{ htmlInput: { 'aria-label': 'Tax agency' } }}
            />
          </Stack>

          {serverError && (
            <Alert severity="error" role="alert">
              {serverError}
            </Alert>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving} color="inherit">
          Cancel
        </Button>
        <Button variant="contained" onClick={() => void handleSave()} disabled={saving}>
          {saving ? 'Saving…' : isEdit ? 'Save tax rate' : 'Add tax rate'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
